import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import type { InventoryImportDraftRow } from '../../types/inventory';
import {
    formatInventoryPartTypeLabel,
    INVENTORY_CATEGORY_OPTIONS,
} from '../../utils/inventoryConstants';

export type CommittedImportExportStatus = 'Succeeded' | 'Failed' | 'Skipped';

export type CommittedImportExportRow = InventoryImportDraftRow & {
    status: CommittedImportExportStatus;
};

const HEADERS = [
    'Name',
    'Mfg. Part Number',
    'Additional Part Numbers',
    'Category',
    'Part Type',
    'Supplier',
    'In-Stock',
    'Avg Unit Cost',
    'Location',
    'Unit',
    'Reorder',
    'Target',
    'Bins',
    'Tags',
    'Status',
    'Failure Reason',
] as const;

function categoryLabel(category: string | null | undefined): string {
    if (!category) return '';
    return INVENTORY_CATEGORY_OPTIONS.find((o) => o.value === category)?.label ?? category;
}

function joinList(values: string[] | undefined): string {
    return (values ?? []).filter(Boolean).join(', ');
}

function failureReason(row: InventoryImportDraftRow): string {
    return (row.issues ?? [])
        .map((i) => i.message)
        .filter(Boolean)
        .join(' · ');
}

function rowValues(row: CommittedImportExportRow): Array<string | number | null> {
    return [
        row.name ?? '',
        row.manufacturerPartNumber ?? '',
        joinList(row.additionalPartNumbers),
        categoryLabel(row.category),
        row.partType ? formatInventoryPartTypeLabel(row.partType) : '',
        row.supplierName ?? '',
        row.initialInStockQuantity ?? '',
        row.unitCost ?? null,
        row.locationTitle ?? '',
        row.unit ?? '',
        row.reorderQuantity ?? null,
        row.targetQuantity ?? null,
        joinList(row.binNames),
        joinList(row.tagNames),
        row.status,
        failureReason(row),
    ];
}

export async function downloadCommittedImportRows(
    rows: CommittedImportExportRow[],
    filename: string,
): Promise<void> {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Import');

    const headerRow = ws.addRow([...HEADERS]);
    headerRow.font = { bold: true };

    rows.forEach((row) => {
        ws.addRow(rowValues(row));
    });

    HEADERS.forEach((header, i) => {
        ws.getColumn(i + 1).width = Math.max(14, Math.min(32, header.length + 4));
    });

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
        new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        filename,
    );
}
